import { useState, useEffect, useCallback } from 'react';
import { taskService } from '../../../services/quantifiedTaskService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface OverdueTaskCountFilterInput {
  foremanAreaNames?: string[];
  serviceAreaName?: string;
}

export interface UseOverdueTaskCountResult {
  overdueCount: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetches overdue quantified task count using planned completion date.
 * A task is considered overdue when plannedCompletionDate is before today.
 */
export function useOverdueTaskCount(
  statuses: string[],
  plannedCompletionDateTo: string,
  filter?: OverdueTaskCountFilterInput | null
): UseOverdueTaskCountResult {
  const [overdueCount, setOverdueCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const foremanAreaNames = filter?.foremanAreaNames;
  const serviceAreaName = filter?.serviceAreaName;
  const hasForemanFilter = !!(foremanAreaNames && foremanAreaNames.length > 0);
  const hasServiceFilter = !!serviceAreaName;

  const fetchCount = useCallback(async () => {
    if (statuses.length === 0) {
      setOverdueCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: Parameters<typeof taskService.getQuantifiedTasksPaginated>[0] = {
        status: statuses,
        limit: 1,
        plannedCompletionDateTo,
      };
      if (hasForemanFilter) {
        params.foremanArea = foremanAreaNames!;
      } else if (hasServiceFilter) {
        params.serviceArea = serviceAreaName!;
      }
      const response = await queueHomeRequest(() => taskService.getQuantifiedTasksPaginated(params));
      setOverdueCount(response.pagination?.totalCount ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch overdue count'));
      setOverdueCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    statuses.join(','),
    plannedCompletionDateTo,
    hasForemanFilter ? foremanAreaNames!.join(',') : '',
    hasServiceFilter ? serviceAreaName : '',
  ]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { overdueCount, loading, error, refetch: fetchCount };
}
